import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from '../services';

const LMS_API_URL = '/api/User/GetAdminUsersForERP';
const HRMS_API_URL = '/api/employees'; // New HRMS endpoint for LMS employees

// Fetch LMS Employees from external API
export const fetchLmsEmployees = createAsyncThunk(
  'lmsEmployees/fetchLmsEmployees',
  async ({ pageNumber, pageSize }, { rejectWithValue }) => {
    try {
      const queryParams = `pageNumber=${pageNumber}&pageSize=${pageSize}`;
      const response = await getRequest(`${LMS_API_URL}?${queryParams}`);
      
      if (response.status !== 200) {
        return rejectWithValue('Failed to fetch LMS employees');
      }
      
      return {
        employees: response.data?.data?.parents || [],
        totalCount: response.data?.data?.totalCount,
        pageNumber,
        pageSize,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Sync LMS Employees to HRMS Backend
export const syncLmsEmployeesToHrms = createAsyncThunk(
  'lmsEmployees/syncLmsEmployeesToHrms',
  async (employees, { rejectWithValue }) => {
    try {
      const response = await hrmsPostRequest(`${HRMS_API_URL}/sync`, employees);
      if (response.status !== 201) {
        return rejectWithValue('Failed to sync LMS employees to HRMS');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update LMS Employee in HRMS (add additional details)
export const updateLmsEmployee = createAsyncThunk(
  'lmsEmployees/updateLmsEmployee',
  async ({ email, updatedData }, { rejectWithValue }) => {
    try {
      const response = await hrmsUpdateRequest(`${HRMS_API_URL}/email/${email}`, updatedData, true);
      if (response.status !== 200) {
        return rejectWithValue('Failed to update LMS employee');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete LMS Employee from HRMS
export const deleteLmsEmployee = createAsyncThunk(
  'lmsEmployees/deleteLmsEmployee',
  async (email, { rejectWithValue }) => {
    try {
      await hrmsDeleteRequest(`${HRMS_API_URL}/email/${email}`);
      return email;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  lmsEmployees: [],
  loading: false,
  error: null,
  pageSize: 50,
  totalCount: 0,
  pageNumber: 1,
};

const lmsEmployeesSlice = createSlice({
  name: 'lmsEmployees',
  initialState,
  reducers: {
    setPageNumber: (state, action) => {
      state.pageNumber = action.payload;
    },
    resetLmsEmployees: (state) => {
      state.lmsEmployees = [];
      state.pageNumber = 1;
      state.totalCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLmsEmployees.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLmsEmployees.fulfilled, (state, action) => {
        state.loading = false;
        state.lmsEmployees = action.payload.employees;
        state.totalCount = action.payload.totalCount;
        state.pageNumber = action.payload.pageNumber;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchLmsEmployees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch LMS employees';
      })
      .addCase(syncLmsEmployeesToHrms.fulfilled, (state, action) => {
        state.lmsEmployees = action.payload;
      })
      .addCase(syncLmsEmployeesToHrms.rejected, (state, action) => {
        state.error = action.payload || 'Failed to sync LMS employees';
      })
      .addCase(updateLmsEmployee.fulfilled, (state, action) => {
        const index = state.lmsEmployees.findIndex((emp) => emp.email === action.payload.email);
        if (index !== -1) {
          state.lmsEmployees[index] = action.payload;
        }
      })
      .addCase(updateLmsEmployee.rejected, (state, action) => {
        state.error = action.payload || 'Failed to update LMS employee';
      })
      .addCase(deleteLmsEmployee.fulfilled, (state, action) => {
        state.lmsEmployees = state.lmsEmployees.filter((emp) => emp.email !== action.payload);
      })
      .addCase(deleteLmsEmployee.rejected, (state, action) => {
        state.error = action.payload || 'Failed to delete LMS employee';
      });
  },
});

export const { setPageNumber, resetLmsEmployees } = lmsEmployeesSlice.actions;
export default lmsEmployeesSlice.reducer;